import { ReactNode } from "react";
import {
  Calendar,
  DateRange,
  Formats,
  SlotInfo,
  View,
  dayjsLocalizer,
} from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import dayjs from "dayjs";
import { theme } from "antd";
import { CalendarEventItem } from "../CalendarView.default";

// https://jquense.github.io/react-big-calendar
const localizer = dayjsLocalizer(dayjs);

// European formats: 24-hour clock, day before month. The localizer defaults are US (AM/PM, M/D).
const TIME = "HH:mm";
const timeRange = ({ start, end }: DateRange) =>
  `${dayjs(start).format(TIME)} – ${dayjs(end).format(TIME)}`;
const formats: Formats = {
  timeGutterFormat: TIME,
  eventTimeRangeFormat: timeRange,
  eventTimeRangeStartFormat: ({ start }: DateRange) => `${dayjs(start).format(TIME)} –`,
  eventTimeRangeEndFormat: ({ end }: DateRange) => `– ${dayjs(end).format(TIME)}`,
  selectRangeFormat: timeRange,
  agendaTimeFormat: TIME,
  agendaTimeRangeFormat: timeRange,
  dayFormat: "ddd DD/MM",
  agendaDateFormat: "ddd DD/MM",
  dayRangeHeaderFormat: ({ start, end }: DateRange) =>
    `${dayjs(start).format("DD MMM")} – ${dayjs(end).format("DD MMM YYYY")}`,
  agendaHeaderFormat: ({ start, end }: DateRange) =>
    `${dayjs(start).format("DD/MM/YYYY")} – ${dayjs(end).format("DD/MM/YYYY")}`,
};

export interface PlatformCalendarProps<T> {
  readonly events: CalendarEventItem<T>[];
  readonly date: Date;
  readonly view: View;
  readonly onNavigate: (date: Date) => void;
  readonly onView: (view: View) => void;
  readonly onSelectEvent?: (item: CalendarEventItem<T>) => void;
  readonly onSelectSlot?: (slot: SlotInfo) => void;
  readonly selectable?: boolean;
  /** Per-view event body (title + whatever badges/sub-labels that view shows). */
  readonly renderEvent: (item: CalendarEventItem<T>) => ReactNode;
  /** Optional per-event styling (e.g. tint origin-created events). */
  readonly eventStyle?: (item: CalendarEventItem<T>) => React.CSSProperties | undefined;
  /** When set, the calendar grid still renders but a centered hint overlays it (e.g. "select a company"). */
  readonly emptyHint?: ReactNode;
  /** When set, the agenda view shows a Description column beside Event. */
  readonly agendaDescription?: (item: CalendarEventItem<T>) => ReactNode;
}

const AGENDA_SPLIT = "grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3 text-left";
const AGENDA_DESCRIPTION_CELL = "min-w-0 border-0 border-l border-solid border-black/10 pl-3";

/**
 * Shared react-big-calendar shell used by both the admin Calendar and the personal My-Calendar: controlled
 * date/view (so the loader can scope to the visible window), a per-view event renderer, optional event
 * styling, and an empty-state hint overlay. The data loading itself lives in `useCalendarEvents`.
 */
function PlatformCalendar<T>(props: PlatformCalendarProps<T>) {
  const { token } = theme.useToken();
  const { agendaDescription } = props;
  return (
    <div className="relative flex flex-auto flex-col overflow-hidden">
      <Calendar
        selectable={props.selectable}
        localizer={localizer}
        formats={formats}
        events={props.events}
        date={props.date}
        view={props.view}
        onNavigate={props.onNavigate}
        onView={props.onView}
        startAccessor="start"
        endAccessor="end"
        components={{
          event: (item: any) => <>{props.renderEvent(item.event)}</>,
          // Week/day views already print the time range above the event; month view doesn't, so
          // the start time leads the pill there as a chip (skipped for all-day events).
          month: {
            event: (item: any) => (
              <span className="flex min-w-0 items-start gap-1.5 py-0.5">
                {!item.event.allDay && (
                  <span className="shrink-0 rounded bg-white/25 px-1 py-px text-[10px] font-semibold leading-tight tracking-wide">
                    {dayjs(item.event.start).format(TIME)}
                  </span>
                )}
                <span className="min-w-0 flex-1">{props.renderEvent(item.event)}</span>
              </span>
            ),
          },
          ...(agendaDescription && {
            agenda: {
              event: (item: any) => (
                <div className={AGENDA_SPLIT}>
                  <div className="min-w-0">{props.renderEvent(item.event)}</div>
                  <div className={`${AGENDA_DESCRIPTION_CELL} truncate`}>
                    {agendaDescription(item.event)}
                  </div>
                </div>
              ),
            },
          }),
        }}
        messages={
          agendaDescription
            ? // RBC's agenda has fixed Date/Time/Event columns, so Description splits the Event
              // column in the same grid as the cells above. RBC renders `event` as a React child
              // (typed as string), which lets the header carry both labels.
              ({
                event: (
                  <div className={AGENDA_SPLIT}>
                    <span>Event</span>
                    <span className={AGENDA_DESCRIPTION_CELL}>Description</span>
                  </div>
                ),
              } as any)
            : undefined
        }
        eventPropGetter={
          props.eventStyle
            ? (event: any) => {
                const style = props.eventStyle!(event);
                return style ? { style } : {};
              }
            : undefined
        }
        onSelectSlot={props.onSelectSlot}
        onSelectEvent={
          props.onSelectEvent
            ? (event: any) => props.onSelectEvent!(event)
            : undefined
        }
      />
      {props.emptyHint && (
        <div className="pointer-events-none absolute inset-0 flex items-start justify-center pt-24">
          <div
            className="px-5 py-3 text-center text-sm font-medium"
            style={{
              backgroundColor: token.colorBgElevated,
              color: token.colorText,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadiusLG,
              boxShadow: token.boxShadowSecondary,
            }}
          >
            {props.emptyHint}
          </div>
        </div>
      )}
    </div>
  );
}

export default PlatformCalendar;
